import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { CeAppBoundList } from '../models/ce-app-bound';
import { CeSelection } from '../models/ce-selection';
import { CeDocInfo } from '../models/ce-doc-info';
import { CeStructureTree } from '../models/ce-structure-tree';
import { ListAppCauses } from '../models/search-edition-params';

/* Http parameters type for critical edition */
export type CeHttpParamsTypes = 'docInfo' | 'structureTree' | 'content' | 'apparatus'
    | 'exegeticNotes' | 'bibliography' | 'translation' | 'appBounds' | 'appCauses' ;

@Injectable()
export class CriticalEditionService {
    private httpHeaders: HttpHeaders;
    private httpParams: Map<CeHttpParamsTypes, HttpParams>;

    private docInfo$: BehaviorSubject<CeDocInfo>;
    private structureTree$: BehaviorSubject<CeStructureTree>;
    private structureTreeSample$: BehaviorSubject<CeStructureTree>;
    private content$: BehaviorSubject<string>;
    private apparatus$: BehaviorSubject<string>;
    private exegeticNotes$: BehaviorSubject<string>;
    private bibliography$: BehaviorSubject<string>;
    private translationEng$: BehaviorSubject<string>;
    private translationLat$: BehaviorSubject<string>;
    private appBounds$: BehaviorSubject<CeAppBoundList>;
    private appSelection$: BehaviorSubject<CeSelection>;
    private contentSelection$: BehaviorSubject<CeSelection>;
    private noteSelection$: BehaviorSubject<CeSelection>;
    private biblioSelection$: BehaviorSubject<CeSelection>;
    private divSelection$: BehaviorSubject<string>;
    private appCauses$: BehaviorSubject<ListAppCauses>;

    constructor(private http: HttpClient) {
        this.init();
    }

    init(): void {
        /* Common headers */
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        this.httpParams = new Map<CeHttpParamsTypes, HttpParams>();

        /* Critical edition document main info http params */
        this.httpParams.set('docInfo', new HttpParams());

        /* Critical edition structure tree main info http params */
        this.httpParams.set('structureTree', new HttpParams());

        /* Critical edition main content http params */
        this.httpParams.set('content', new HttpParams()/*.set('fields', 'body')*/);

        /* Critical edition apparatus http params */
        this.httpParams.set('apparatus', new HttpParams()/*.set('fields', 'listApp')*/);

        /* Critical edition exegetic notes http params */
        this.httpParams.set('exegeticNotes', new HttpParams());

        /* Critical edition bibliography http params */
        this.httpParams.set('bibliography', new HttpParams());

        /* Critical edition translation http params */
        this.httpParams.set('translation', new HttpParams());

        /* Critical edition (content) link bounds http params */
        this.httpParams.set('appBounds', new HttpParams());

        /* Critical edition apparatus reading causes http params */
        this.httpParams.set('appCauses', new HttpParams());

        /* Observables on critical edition data */
        this.docInfo$ = new BehaviorSubject<CeDocInfo>(new CeDocInfo());
        this.structureTree$ = new BehaviorSubject<CeStructureTree>([]);
        this.structureTreeSample$ = new BehaviorSubject<CeStructureTree>([]); // reserved structure for samples due to use in main menus
        this.content$ = new BehaviorSubject<string>('');
        this.apparatus$ = new BehaviorSubject<string>('');
        this.exegeticNotes$ = new BehaviorSubject<string>('');
        this.bibliography$ = new BehaviorSubject<string>('');
        this.translationEng$ = new BehaviorSubject<string>('');
        this.translationLat$ = new BehaviorSubject<string>('');
        this.appBounds$ = new BehaviorSubject<CeAppBoundList>([]);
        this.appSelection$ = new BehaviorSubject<CeSelection>(new CeSelection());
        this.contentSelection$ = new BehaviorSubject<CeSelection>(new CeSelection());
        this.noteSelection$ = new BehaviorSubject<CeSelection>(new CeSelection());
        this.biblioSelection$ = new BehaviorSubject<CeSelection>(new CeSelection());
        this.divSelection$ = new BehaviorSubject<string>('');
        this.appCauses$ = new BehaviorSubject<ListAppCauses>([]);
    }

    /* Critical edition full endpoint */
    private getEndpoint(isSample: boolean): string {
        return environment.endpoints.admin
            + '/ced/'
            + (isSample ? environment.criticalEdition.type.sample : environment.criticalEdition.type.public);
    }

    /* Critical edition document main info API call */
    private loadDocInfo(cedId: string, isSample: boolean): void {
        this.http.get<CeDocInfo>(
            this.getEndpoint(isSample) + '/info/' + cedId,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('docInfo')
            })
            .subscribe(
                response => this.docInfo$.next(response),
                error => this.docInfo$.error(error)
            );
    }

    /* Critical edition structure tree main info API call */
    private loadStructureTree(isSample: boolean): void {
        this.http.get<CeStructureTree>(
            this.getEndpoint(isSample) + '/tree',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('structureTree')
            })
            .subscribe(
                response => isSample ? this.structureTreeSample$.next(response) : this.structureTree$.next(response),
                error => isSample ? this.structureTreeSample$.error(error) : this.structureTree$.error(error)
            );
    }

    /* Critical edition main content API call */
    private loadContent(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/html/' + cedId + '/body',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('content'),
                responseType: 'text'
            })
            .subscribe(
                response => this.content$.next(response),
                error => this.content$.error(error)
            );
    }

    /* Critical edition apparatus (reading list) API call */
    private loadApparatus(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/html/' + cedId + '/listApp',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('apparatus'),
                responseType: 'text'
            })
            .subscribe(
                response => this.apparatus$.next(response),
                error => this.apparatus$.error(error)
            );
    }

    /* Critical edition exegetic notes API call */
    private loadExegeticNotes(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/html/' + cedId + '/notes',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('exegeticNotes'),
                responseType: 'text'
            })
            .subscribe(
                response => this.exegeticNotes$.next(response),
                error => this.exegeticNotes$.error(error)
            );
    }

    /* Critical edition bibliography API call */
    private loadBibliography(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/html/' + cedId + '/biblio',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('bibliography'),
                responseType: 'text'
            })
            .subscribe(
                response => this.bibliography$.next(response),
                error => this.bibliography$.error(error)
            );
    }

    /* Critical edition english translation API call */
    private loadTranslationEng(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/trans/en/' + cedId + '/trans_en',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('translation'),
                responseType: 'text'
            })
            .subscribe(
                response => this.translationEng$.next(response),
                error => this.translationEng$.error(error)
            );
    }

    /* Critical edition latin translation API call */
    private loadTranslationLat(cedId: string, isSample: boolean): void {
        this.http.get(
            this.getEndpoint(isSample) + '/trans/la/' + cedId + '/trans_la',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('translation'),
                responseType: 'text'
            })
            .subscribe(
                response => this.translationLat$.next(response),
                error => this.translationLat$.error(error)
            );
    }

    /* Critical edition apparatus (section) bounds API call */
    private loadAppBounds(cedId: string, isSample: boolean): void {
        this.http.get<CeAppBoundList>(
            this.getEndpoint(isSample) + '/app/bounds/' + cedId,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('appBounds')
            })
            .subscribe(
                response => this.appBounds$.next(response),
                error => this.appBounds$.error(error)
            );
    }

    /* Critical edition apparatus reading causes API call */
    private loadAppCauses(cedId: string, isSample: boolean): void {
        this.http.get<ListAppCauses>(
            this.getEndpoint(isSample) + '/app/causes/' + cedId,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('appCauses')
            })
            .subscribe(
                response => this.appCauses$.next(response),
                error => this.appCauses$.error(error)
            );
    }

    /* Get critical edition document main info (HTML) */
    getDocInfo(cedId: string, isSample = false): Observable<CeDocInfo> {
        this.loadDocInfo(cedId, isSample);
        return this.docInfo$.asObservable();
    }

    /* Get critical edition structure tree */
    getStructureTree(isSample = false): Observable<CeStructureTree> {
        this.loadStructureTree(isSample);
        return isSample ? this.structureTreeSample$.asObservable() : this.structureTree$.asObservable();
    }

    /* Get critical edition main content (HTML) */
    getContent(cedId: string, isSample = false): Observable<string> {
        this.loadContent(cedId, isSample);
        return this.content$.asObservable();
    }

    /* Get critical edition apparatus (HTML) */
    getApparatus(cedId: string, isSample = false): Observable<string> {
        this.loadApparatus(cedId, isSample);
        return this.apparatus$.asObservable();
    }

    /* Get critical edition exegetic notes (HTML) */
    getExegeticNotes(cedId: string, isSample = false): Observable<string> {
        this.loadExegeticNotes(cedId, isSample);
        return this.exegeticNotes$.asObservable();
    }

    /* Get critical edition bibliography (HTML) */
    getBibliography(cedId: string, isSample = false): Observable<string> {
        this.loadBibliography(cedId, isSample);
        return this.bibliography$.asObservable();
    }

    /* Get critical edition english translation (HTML) */
    getTranslationEng(cedId: string, isSample = false): Observable<string> {
        this.loadTranslationEng(cedId, isSample);
        return this.translationEng$.asObservable();
    }

    /* Get critical edition latin translation (HTML) */
    getTranslationLat(cedId: string, isSample = false): Observable<string> {
        this.loadTranslationLat(cedId, isSample);
        return this.translationLat$.asObservable();
    }

    /* Get critical edition apparatus (section) bounds */
    getAppBounds(cedId: string, isSample = false): Observable<CeAppBoundList> {
        this.loadAppBounds(cedId, isSample);
        return this.appBounds$.asObservable();
    }

    /* Get critical edition apparatus reading causes */
    getAppCauses(cedId: string, isSample = false): Observable<ListAppCauses> {
        this.loadAppCauses(cedId, isSample);
        return this.appCauses$.asObservable();
    }

    /* Get critical apparatus selection for current view */
    getAppSelection(): Observable<CeSelection> {
        return this.appSelection$.asObservable();
    }

    /* Set critical apparatus selection for current view */
    setAppSelection(appSelection: CeSelection): void {
        this.appSelection$.next(appSelection);
    }

    /* Get critical text content selection for current view */
    getContentSelection(): Observable<CeSelection> {
        return this.contentSelection$.asObservable();
    }

    /* Set critical text content selection for current view */
    setContentSelection(contentSelection: CeSelection): void {
        this.contentSelection$.next(contentSelection);
    }

    /* Get exegetic note selection for current view */
    getNoteSelection(): Observable<CeSelection> {
        return this.noteSelection$.asObservable();
    }

    /* Set exegetic note selection for current view */
    setNoteSelection(noteSelection: CeSelection): void {
        this.noteSelection$.next(noteSelection);
    }

    /* Get selected bibliography for current view */
    getBiblioSelection(): Observable<CeSelection> {
        return this.biblioSelection$.asObservable();
    }

    /* Set selected bibliography for current view */
    setBiblioSelection(biblioSelection: CeSelection): void {
        this.biblioSelection$.next(biblioSelection);
    }

    /* Get selected structural division for current view */
    getDivSelection(): Observable<string> {
        return this.divSelection$.asObservable();
    }

    /* Set selected structural division for current view */
    setDivSelection(divSelection: string): void {
        this.divSelection$.next(divSelection);
    }

    /* Get critical edition structure (TEI-XML content) */
    downloadStructure(isSample: boolean, struct: string): Observable<string> {
        return this.http.get(
            this.getEndpoint(isSample) + '/download/' + struct,
            {
                headers: this.httpHeaders,
                responseType: 'blob'
            })
            .map(blob => {
                return URL.createObjectURL(blob);
            });
    }

    /* Get critical edition translation structure (TEI-XML content) */
    downloadTranslationStructure(isSample: boolean, lang: string, struct: string): Observable<string> {
        return this.http.get(
            this.getEndpoint(isSample) + '/download/trans/' + lang + '/' + struct,
            {
                headers: this.httpHeaders,
                responseType: 'blob'
            })
            .map(blob => {
                return URL.createObjectURL(blob);
            });
    }

}



// WEBPACK FOOTER //
// ./src/app/services/critical-edition.service.ts